/**
 * CardsDeviceEnumList Component
 * 
 * Displays a comprehensive device enum card with detailed information including:
 * - Device icon/image
 * - Name and type
 * - Operating system badge
 * - Subtitle/description
 * - Status badges (Private, Alpha, Beta)
 * - Sensor and I/O counts
 * - Hardware components list
 * - Links to GitHub and web resources
 * - Action buttons (edit, delete)
 * 
 * @param {Object} deviceEnum - The device enum data object
 * @param {boolean} isSmall - Whether to display in small (2-column) or large (1-column) layout
 */
import React, { useState } from "react";

import { firebaseStore } from "../../firebase/firestore";
import { deleteDoc, doc } from "firebase/firestore";
import {
  ref,
  getDownloadURL,
  deleteObject,
} from "firebase/storage";
import { firebaseStorage } from "../../firebase/storage";
import { 
  IconButton, 
  Snackbar, 
  Paper,
  Chip,
  Tooltip,
  Box,
  Typography,
  Divider,
  Link as MuiLink
} from "@mui/material";
import ModeEditOutlineOutlinedIcon from "@mui/icons-material/ModeEditOutlineOutlined";
import DeleteOutlineOutlinedIcon from "@mui/icons-material/DeleteOutlineOutlined";
import LockIcon from "@mui/icons-material/Lock";
import ScienceIcon from "@mui/icons-material/Science";
import BugReportIcon from "@mui/icons-material/BugReport";
import SensorsIcon from "@mui/icons-material/Sensors";
import SettingsInputComponentIcon from "@mui/icons-material/SettingsInputComponent";
import GitHubIcon from "@mui/icons-material/GitHub";
import LanguageIcon from "@mui/icons-material/Language";
import MemoryIcon from "@mui/icons-material/Memory";

import { ImageLoader } from "../img/ImageLoader";
import EditDeviceEnum from "../../../views/owner/deviceEnums/components/EditDeviceEnum";

import "./UiCards.css";

function CardsDeviceEnumList({ deviceEnum, isSmall }: any) {
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [isSnackbarOpen, setIsSnackbarOpen] = useState(false);
  
  const style = {
    paper: {
      width: isSmall ? "49%" : "100%",
      display: "flex",
      flexDirection: "column",
      justifyContent: "flex-start",
      alignItems: "stretch",
      marginTop: "20px",
      padding: "16px",
      transition: "all 0.2s ease-in-out",
      "&:hover": {
        boxShadow: "0 4px 12px rgba(68, 169, 154, 0.2)",
        transform: "translateY(-2px)"
      }
    },
  };

  const handleClose = () => {
    setIsDialogOpen(false);
  };

  /**
   * Checks if a storage reference exists by attempting to get its download URL
   * @param {any} imageRef - Firebase storage reference
   * @returns {Promise<boolean>} Whether the image exists
   */
  const checkIfLogoExists = async (imageRef: any) => {
    try {
      await getDownloadURL(imageRef);
      return true;
    } catch (err) {
      return false;
    }
  };

  /**
   * Deletes the device enum and all associated images from Firestore and Storage
   * Handles cleanup of:
   * - Icon image
   * - Image array (multiple images)
   * - Firestore document
   */
  const handleDelete = async () => {
    try {
      setIsDialogOpen(false);
      // Check if icon is not '' and delete it from storage
      if (deviceEnum.icon !== "") {
        const iconPathString = `cloud/deviceEnums/${deviceEnum.id}/${deviceEnum.id}-icon.png`;
        const storageRef = ref(firebaseStorage, iconPathString);
        if (await checkIfLogoExists(storageRef)) {
          await deleteObject(storageRef);
        }
      }
      // Check if images is not [] and delete them from storage
      if (deviceEnum.images.length > 0) {
        deviceEnum.images.map(async (image: string, index: number) => {
          const imagePathString = `cloud/deviceEnums/${deviceEnum.id}/${
            deviceEnum.id
          }-${index + 1}.jpeg`;
          const storageRef = ref(firebaseStorage, imagePathString);
          if (await checkIfLogoExists(storageRef)) {
            await deleteObject(storageRef);
          }
        });
      }
      await deleteDoc(doc(firebaseStore, "deviceEnums", deviceEnum.id));
      setIsSnackbarOpen(true);
    } catch (error) {
      console.error("Error removing document: ", error);
    }
  };

  return (
    <Paper sx={style.paper} elevation={2}>
      {/* Header Section with Image and Title */}
      <Box display="flex" gap={2} mb={2}>
        {/* Device Icon */}
        <Box 
          sx={{ 
            width: "120px", 
            height: "120px", 
            flexShrink: 0,
            borderRadius: "8px",
            overflow: "hidden",
            border: "1px solid #e0e0e0"
          }}
        >
          <ImageLoader
            imgUrl={deviceEnum.icon}
            radiusStyle={"8px"}
          />
        </Box>

        {/* Title and Type Section */}
        <Box flex={1} display="flex" flexDirection="column" justifyContent="space-between">
          <Box>
            <Typography 
              variant="h6" 
              sx={{ 
                fontWeight: 600, 
                color: "#39393c",
                mb: 0.5,
                fontSize: "1.1rem"
              }}
            >
              {deviceEnum.name}
            </Typography>
            <Typography 
              variant="body2" 
              sx={{ 
                fontStyle: "italic", 
                color: "#666",
                mb: 1
              }}
            >
              {deviceEnum.type}
            </Typography>
            
            {/* Status Badges */}
            <Box display="flex" gap={0.5} flexWrap="wrap" mb={1}>
              <Chip 
                label={deviceEnum.os} 
                size="small" 
                sx={{ 
                  backgroundColor: "#44a99a",
                  color: "#fff",
                  fontWeight: 500
                }}
              />
              {deviceEnum.private && (
                <Chip 
                  icon={<LockIcon style={{ color: "#fff" }} />}
                  label="Private" 
                  size="small" 
                  sx={{ 
                    backgroundColor: "#757575",
                    color: "#fff"
                  }}
                />
              )}
              {deviceEnum.alpha && (
                <Chip 
                  icon={<ScienceIcon style={{ color: "#fff" }} />}
                  label="Alpha" 
                  size="small" 
                  sx={{ 
                    backgroundColor: "#ff9800",
                    color: "#fff"
                  }}
                />
              )}
              {deviceEnum.beta && (
                <Chip 
                  icon={<BugReportIcon style={{ color: "#fff" }} />}
                  label="Beta" 
                  size="small" 
                  sx={{ 
                    backgroundColor: "#2196f3",
                    color: "#fff"
                  }}
                />
              )}
            </Box>
          </Box>

          {/* Action Buttons */}
          <Box display="flex" gap={1}>
            <Tooltip title="Edit">
              <IconButton
                color="inherit"
                size="small"
                onClick={() => setIsDialogOpen(true)}
              >
                <ModeEditOutlineOutlinedIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            <Tooltip title="Delete">
              <IconButton 
                sx={{ color: "error.main" }} 
                size="small" 
                onClick={handleDelete}
              >
                <DeleteOutlineOutlinedIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </Box>
        </Box>
      </Box>

      <Divider sx={{ mb: 2 }} />

      {/* Subtitle/Description */}
      {deviceEnum.subtitle && (
        <Typography 
          variant="body2" 
          sx={{ 
            color: "#555",
            mb: 2,
            lineHeight: 1.5
          }}
        >
          {deviceEnum.subtitle}
        </Typography>
      )}

      {/* Sensors Section */}
      {deviceEnum.sensors && deviceEnum.sensors.length > 0 && (
        <Box mb={2}>
          <Typography 
            variant="subtitle2" 
            sx={{ 
              color: "#44a99a", 
              fontWeight: 600,
              mb: 1,
              display: "flex",
              alignItems: "center",
              gap: 0.5
            }}
          >
            <SensorsIcon fontSize="small" /> Sensors ({deviceEnum.sensors.length})
          </Typography>
          <Box display="flex" gap={0.5} flexWrap="wrap">
            {deviceEnum.sensors.slice(0, 3).map((sensor: any, index: number) => (
              <Chip
                key={index}
                label={`${sensor.name}${sensor.unit ? ` (${sensor.unit})` : ''}`}
                size="small"
                variant="outlined"
                sx={{ fontSize: "0.75rem" }}
              />
            ))}
            {deviceEnum.sensors.length > 3 && (
              <Chip
                label={`+${deviceEnum.sensors.length - 3} more`}
                size="small"
                sx={{ 
                  backgroundColor: "#e0e0e0",
                  fontSize: "0.75rem"
                }}
              />
            )}
          </Box>
        </Box>
      )}

      {/* I/O Section */}
      {deviceEnum.io && deviceEnum.io.length > 0 && (
        <Box mb={2}>
          <Typography 
            variant="subtitle2" 
            sx={{ 
              color: "#44a99a", 
              fontWeight: 600,
              mb: 1,
              display: "flex",
              alignItems: "center",
              gap: 0.5
            }}
          >
            <SettingsInputComponentIcon fontSize="small" /> I/O ({deviceEnum.io.length})
          </Typography>
          <Box display="flex" gap={0.5} flexWrap="wrap">
            {deviceEnum.io.slice(0, 3).map((io: any, index: number) => (
              <Chip
                key={index}
                label={`${io.name} (${io.type})`}
                size="small"
                variant="outlined"
                sx={{ fontSize: "0.75rem" }}
              />
            ))}
            {deviceEnum.io.length > 3 && (
              <Chip
                label={`+${deviceEnum.io.length - 3} more`}
                size="small"
                sx={{ 
                  backgroundColor: "#e0e0e0",
                  fontSize: "0.75rem"
                }}
              />
            )}
          </Box>
        </Box>
      )}

      {/* Hardware Section */}
      {deviceEnum.hardware && deviceEnum.hardware.length > 0 && (
        <Box mb={2}>
          <Typography 
            variant="subtitle2" 
            sx={{ 
              color: "#44a99a", 
              fontWeight: 600,
              mb: 1,
              display: "flex",
              alignItems: "center",
              gap: 0.5
            }}
          >
            <MemoryIcon fontSize="small" /> Hardware Components
          </Typography>
          <Box display="flex" gap={0.5} flexWrap="wrap">
            {deviceEnum.hardware.slice(0, 4).map((hardware: string, index: number) => (
              <Chip
                key={index}
                label={hardware}
                size="small"
                sx={{ 
                  backgroundColor: "#f0f0f0",
                  fontSize: "0.75rem"
                }}
              />
            ))}
            {deviceEnum.hardware.length > 4 && (
              <Chip
                label={`+${deviceEnum.hardware.length - 4} more`}
                size="small"
                sx={{ 
                  backgroundColor: "#e0e0e0",
                  fontSize: "0.75rem"
                }}
              />
            )}
          </Box>
        </Box>
      )}

      {/* Links Section */}
      <Box display="flex" gap={2} flexWrap="wrap" alignItems="center">
        {deviceEnum.github && (
          <Tooltip title="View on GitHub">
            <MuiLink
              href={deviceEnum.github}
              target="_blank"
              rel="noopener noreferrer"
              sx={{ 
                display: "flex", 
                alignItems: "center", 
                gap: 0.5,
                color: "#333",
                textDecoration: "none",
                "&:hover": {
                  color: "#44a99a"
                }
              }}
            >
              <GitHubIcon fontSize="small" />
              <Typography variant="caption">GitHub</Typography>
            </MuiLink>
          </Tooltip>
        )}
        {deviceEnum.web && (
          <Tooltip title="Visit Website">
            <MuiLink
              href={deviceEnum.web}
              target="_blank"
              rel="noopener noreferrer"
              sx={{ 
                display: "flex", 
                alignItems: "center", 
                gap: 0.5,
                color: "#333",
                textDecoration: "none",
                "&:hover": {
                  color: "#44a99a"
                }
              }}
            >
              <LanguageIcon fontSize="small" />
              <Typography variant="caption">Website</Typography>
            </MuiLink>
          </Tooltip>
        )}
      </Box>

      {/* Snackbar for Delete Confirmation */}
      <Snackbar
        open={isSnackbarOpen}
        message={`${deviceEnum.name} deleted successfully`}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        autoHideDuration={5000}
        onClose={() => setIsSnackbarOpen(false)}
      />

      {/* Edit Dialog */}
      {isDialogOpen && (
        <EditDeviceEnum
          deviceEnumInitData={deviceEnum}
          isDialogOpen={isDialogOpen}
          handleClose={handleClose}
        />
      )}
    </Paper>
  );
}

export default CardsDeviceEnumList;
